/**
 * Types and functions for working with 3d axis-aligned bounding boxes (AABB3)
 * @module
 */
import { intervalsOverlap } from "./aabb2";
import type { AABB3 } from "./aabb3Types";
import { aabb3New } from "./aabb3Types";
import { DIST_EPSILON } from "./core";
import { PlaneSide, vec3PlaneSide } from "./plane";
import type { Plane } from "./planeTypes";
import type { PolyLine3 } from "./polyLine3";
import type { Vec3 } from "./vec3";
import { vec3Result } from "./vec3";

const { min, max, sqrt } = Math;

/**
 * Initializes a degenerate AABB3 that is useful for expansion with aabb3Union or aabb3Expand
 *
 * @returns new AABB3
 */
export function aabb3Init(): AABB3 {
  return {
    minX: Infinity,
    minY: Infinity,
    minZ: Infinity,
    maxX: -Infinity,
    maxY: -Infinity,
    maxZ: -Infinity,
  };
}

export function aabb3Copy({ minX, minY, minZ, maxX, maxY, maxZ }: AABB3): AABB3 {
  return aabb3New(minX, minY, minZ, maxX, maxY, maxZ);
}

export function aabb3TranslateCopy(a: AABB3, tx: number, ty: number, tz: number): AABB3 {
  return aabb3New(a.minX + tx, a.minY + ty, a.minZ + tz, a.maxX + tx, a.maxY + ty, a.maxZ + tz);
}

export function aabb3Scale(a: AABB3, s: number): AABB3 {
  a.minX *= s;
  a.maxX *= s;
  a.minY *= s;
  a.maxY *= s;
  a.minZ *= s;
  a.maxZ *= s;
  return a;
}

export function aabb3Overlaps(a: AABB3, b: AABB3, epsilon = DIST_EPSILON): boolean {
  return (
    intervalsOverlap(a.minX, a.maxX, b.minX, b.maxX, epsilon) &&
    intervalsOverlap(a.minY, a.maxY, b.minY, b.maxY, epsilon) &&
    intervalsOverlap(a.minZ, a.maxZ, b.minZ, b.maxZ, epsilon)
  );
}

export function aabb3ContainsVec3(bb: AABB3, x: number, y: number, z: number): boolean {
  return x >= bb.minX && y >= bb.minY && z >= bb.minZ && x <= bb.maxX && y <= bb.maxY && z <= bb.maxZ;
}

export function aabb3ContainsVec3ExclusiveMax(bb: AABB3, x: number, y: number, z: number): boolean {
  return x >= bb.minX && y >= bb.minY && z >= bb.minZ && x < bb.maxX && y < bb.maxY && z < bb.maxZ;
}

export function aabb3Union(a: AABB3, b: AABB3): AABB3 {
  return aabb3New(
    min(a.minX, b.minX),
    min(a.minY, b.minY),
    min(a.minZ, b.minZ),
    max(a.maxX, b.maxX),
    max(a.maxY, b.maxY),
    max(a.maxZ, b.maxZ),
  );
}

export function aabb3AddPoint(a: AABB3, x: number, y: number, z: number): AABB3 {
  a.minX = x < a.minX ? x : a.minX;
  a.minY = y < a.minY ? y : a.minY;
  a.minZ = z < a.minZ ? z : a.minZ;
  a.maxX = x > a.maxX ? x : a.maxX;
  a.maxY = y > a.maxY ? y : a.maxY;
  a.maxZ = z > a.maxZ ? z : a.maxZ;
  return a;
}

export function aabb3DiagLength(a: AABB3): number {
  const dx = a.maxX - a.minX;
  const dy = a.maxY - a.minY;
  const dz = a.maxZ - a.minZ;
  return sqrt(dx * dx + dy * dy + dz * dz);
}

export function aabb3XLength(a: AABB3): number {
  return a.maxX - a.minX;
}

export function aabb3YLength(a: AABB3): number {
  return a.maxY - a.minY;
}

export function aabb3ZLength(a: AABB3): number {
  return a.maxZ - a.minZ;
}

export function aabb3IsValid(a: AABB3): boolean {
  return a.maxX >= a.minX && a.maxY >= a.minY && a.maxZ >= a.minZ;
}

export function aabb3Expand(a: AABB3, x: number, y: number, z: number): void {
  a.minX = a.minX < x ? a.minX : x;
  a.minY = a.minY < y ? a.minY : y;
  a.minZ = a.minZ < z ? a.minZ : z;
  a.maxX = a.maxX > x ? a.maxX : x;
  a.maxY = a.maxY > y ? a.maxY : y;
  a.maxZ = a.maxZ > z ? a.maxZ : z;
}

export function aabb3Grow(a: AABB3, deltaX: number, deltaY: number, deltaZ: number): void {
  a.minX -= deltaX;
  a.maxX += deltaX;
  a.minY -= deltaY;
  a.maxY += deltaY;
  a.minZ -= deltaZ;
  a.maxZ += deltaZ;
}

export function aabb3Compare(a: AABB3, b: AABB3): boolean {
  return (
    a.minX === b.minX &&
    a.maxX === b.maxX &&
    a.minY === b.minY &&
    a.maxY === b.maxY &&
    a.minZ === b.minZ &&
    a.maxZ === b.maxZ
  );
}

function axisDist(k: number, min: number, max: number) {
  if (k < min) {
    return min - k;
  } else if (k <= max) {
    return 0;
  } else {
    return k - max;
  }
}

export function aabb3BoxDistSquared(a: AABB3, x: number, y: number, z: number): number {
  const dx = axisDist(x, a.minX, a.maxX);
  const dy = axisDist(y, a.minY, a.maxY);
  const dz = axisDist(z, a.minZ, a.maxZ);
  return dx * dx + dy * dy + dz * dz;
}

export function aabb3BoxDist(a: AABB3, x: number, y: number, z: number): number {
  return sqrt(aabb3BoxDistSquared(a, x, y, z));
}

export function aabb3Center(a: AABB3): Vec3 {
  const { minX, minY, minZ, maxX, maxY, maxZ } = a;
  vec3Result[0] = (minX + maxX) / 2;
  vec3Result[1] = (minY + maxY) / 2;
  vec3Result[2] = (minZ + maxZ) / 2;
  return vec3Result;
}

export function aabb3Volume(a: AABB3) {
  return (a.maxZ - a.minZ) * (a.maxY - a.minY) * (a.maxX - a.minX);
}

export function aabb3ByTwoPoints(ax: number, ay: number, az: number, bx: number, by: number, bz: number): AABB3 {
  return aabb3New(
    Math.min(ax, bx),
    Math.min(ay, by),
    Math.min(az, bz),
    Math.max(ax, bx),
    Math.max(ay, by),
    Math.max(az, bz),
  );
}

export function aabb3ByPoints(points: Vec3[]): AABB3 {
  const [x1, y1, z1] = points[0];
  const bb = aabb3New(x1, y1, z1, x1, y1, z1);
  for (let i = 1, l = points.length; i < l; i += 1) {
    const [x, y, z] = points[i];
    aabb3AddPoint(bb, x, y, z);
  }

  return bb;
}

export function aabb3ByArray(pls: Array<Float64Array | Float32Array | number[]>): AABB3 | null {
  if (pls.length === 0) {
    return null;
  }
  const aabb = aabb3Init();

  for (const line of pls) {
    for (let i = 0, l = line.length; i < l; i += 3) {
      aabb3AddPoint(aabb, line[i], line[i + 1], line[i + 2]);
    }
  }
  return aabb;
}

export function aabb3ByPolyLine3s(pls: PolyLine3[]): AABB3 | null {
  return aabb3ByArray(pls);
}

export function aabb3ClassifyPlaneSide(aabb: AABB3, plane: Plane, tol: number = 1e-6): PlaneSide {
  const { minX, minY, minZ, maxX, maxY, maxZ } = aabb;
  const { originX, originY, originZ, uX, uY, uZ, vX, vY, vZ } = plane;

  // Calculate plane normal from u and v vectors
  const nx = uY * vZ - uZ * vY;
  const ny = uZ * vX - uX * vZ;
  const nz = uX * vY - uY * vX;

  // Get all 8 vertices of the AABB
  const vertices = [
    minX,
    minY,
    minZ,
    minX,
    minY,
    maxZ,
    minX,
    maxY,
    minZ,
    minX,
    maxY,
    maxZ,
    maxX,
    minY,
    minZ,
    maxX,
    minY,
    maxZ,
    maxX,
    maxY,
    minZ,
    maxX,
    maxY,
    maxZ,
  ];

  // Check which side each vertex is on
  let frontCount = 0;
  let backCount = 0;
  let coplanarCount = 0;

  for (let i = 0; i < vertices.length; i += 3) {
    const x = vertices[i];
    const y = vertices[i + 1];
    const z = vertices[i + 2];

    const side = vec3PlaneSide(x, y, z, originX, originY, originZ, nx, ny, nz, tol);

    switch (side) {
      case PlaneSide.FRONT:
        frontCount++;
        break;
      case PlaneSide.BACK:
        backCount++;
        break;
      case PlaneSide.COPLANAR:
        coplanarCount++;
        break;
      case PlaneSide.INTERSECTS:
        // never happens
        break;
    }
  }

  if (coplanarCount === 0) {
    if (frontCount > 0 && backCount > 0) {
      return PlaneSide.INTERSECTS;
    }

    return frontCount > 0 ? PlaneSide.FRONT : PlaneSide.BACK;
  }

  if (frontCount > 0 && backCount === 0) {
    return PlaneSide.FRONT;
  }

  if (frontCount === 0 && backCount > 0) {
    return PlaneSide.BACK;
  }

  return PlaneSide.COPLANAR;
}

export function isAABB3Valid(a?: AABB3): a is AABB3 {
  if (!a) {
    return false;
  }
  if (
    Object.values(a).some((v) => {
      if (typeof v === "number") {
        return isNaN(v);
      }
      return false;
    })
  ) {
    return false;
  }
  return true;
}

export function aabb3Correct(a: AABB3): AABB3 {
  if (a.minX > a.maxX) {
    [a.minX, a.maxX] = [a.maxX, a.minX];
  }
  if (a.minY > a.maxY) {
    [a.minY, a.maxY] = [a.maxY, a.minY];
  }
  if (a.minZ > a.maxZ) {
    [a.minZ, a.maxZ] = [a.maxZ, a.minZ];
  }
  return a;
}
